<lib-breadcrumb [list]="breadcrumbs()"></lib-breadcrumb>
<div class="text-lg mb-5 font-bold">Judgement Schedule</div>

<form [formGroup]="searchForm" class="bg-fieldset p-4 rounded-lg shadow-sm border border-gray-200"
  (ngSubmit)="searchHandler()">
  <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">

    <div class="w-full">
      <mat-label class="block mb-1 text-sm font-medium">Judgement Phase</mat-label>
      <mat-form-field class="w-full" appearance="outline">
        <mat-select formControlName="judgementPhaseName">
          @for (phase of judgementPhases; track phase.value) {
          <mat-option [value]="phase.value">{{ phase.key }}</mat-option>
          }
        </mat-select>
        <mat-error>Field value required</mat-error>
      </mat-form-field>
    </div>

    <div class="w-full" *ngIf="this.getCourtNameData()?.courtId !== 1 && this.getCourtNameData()?.courtId !== 2">
      <mat-label class="block mb-1 text-sm font-medium">{{ isSupremeFlag || isAppeal ? 'Appeal' : 'Case' }}
        Category</mat-label>
      <mat-form-field class="w-full" appearance="outline">
        <mat-select formControlName="caseCategory">
          @for (category of caseCategories; track $index) {
          <mat-option [value]="category.key">{{category.value}}</mat-option>
          }
        </mat-select>
        <mat-error>Field value required</mat-error>
      </mat-form-field>
    </div>

    <div class="flex gap-3 w-full mt-1 md:mt-5 justify-start">
      <button type="reset" mat-stroked-button color="primary" class="mr-4 w-full md:w-auto"
        (click)="resetHandler()">Reset</button>
      <button type="submit" mat-flat-button color="primary" class="w-full md:w-auto"
        [disabled]="searchForm.invalid">Search</button>
    </div>
  </div>
</form>

<div class="flex flex-col pb-3 w-full mt-4">
  <div class="flex-grow bg-fieldset rounded shadow-sm overflow-hidden">
    <div class="flex px-4 py-3 border-slate-400 border-b items-center bg-gray-50">
      <h2 class="text-lg font-semibold">{{ isSupremeFlag || isAppeal ? 'Appeals' : 'Cases' }} for Judgement Schedule
      </h2>
    </div>

    <div class="px-0 md:px-4 py-2">
      @if(!isLoading) {
      <div class="table-scroll-wrapper">
        <cdk-virtual-scroll-viewport itemSize="48" class="h-60 border w-full min-w-[800px]" maxItemsToRender="10">
          <table mat-table [dataSource]="dataSource" matSort class="case-summery-table virtual-scroll w-full">

            <!-- Case Number Column -->
            <ng-container matColumnDef="caseNumber">
              <th mat-header-cell *matHeaderCellDef mat-sort-header class="whitespace-nowrap px-4"> {{ isSupremeFlag ||
                isAppeal ? 'Appeal' : 'Case' }} Number </th>
              <td mat-cell *matCellDef="let row" class="px-4 font-medium">
                <span (click)="getCaseDetails(row)"
                  class="cursor-pointer text-primary hover:underline font-medium text-sm">
                  {{ row.caseNumber || '—' }}
                </span>
              </td>
            </ng-container>

            <!-- Plaintiff Column -->
            <ng-container matColumnDef="division">
              <th mat-header-cell *matHeaderCellDef mat-sort-header class="whitespace-nowrap px-4"> Division </th>
              <td mat-cell *matCellDef="let row" class="px-4">
                {{row.divisionName || '—'}}
              </td>
            </ng-container>

            <!-- Respondent Column -->
            <ng-container matColumnDef="court">
              <th mat-header-cell *matHeaderCellDef mat-sort-header class="whitespace-nowrap px-4"> Court </th>
              <td mat-cell *matCellDef="let row" class="px-4">
                {{row.courtUnitName || '—'}}
              </td>
            </ng-container>

            <!-- Judgement Date Column -->
            <ng-container matColumnDef="judgementDate">
              <th mat-header-cell *matHeaderCellDef mat-sort-header class="whitespace-nowrap px-4"> Judgement Date </th>
              <td mat-cell *matCellDef="let row" class="px-4">
                @if(row.editable) {
                <div class="flex items-center justify-start h-full w-full">
                  <mat-form-field appearance="outline" class="compact-date-field w-full max-w-[140px]">
                    <input [(ngModel)]="row.editedCtrlVal" matInput [matDatepicker]="picker" placeholder="Choose date">
                    <mat-datepicker-toggle matIconSuffix [for]="picker"></mat-datepicker-toggle>
                    <mat-datepicker #picker></mat-datepicker>
                  </mat-form-field>
                </div>
                } @else {
                <div class="flex items-center justify-start h-full px-2">
                  <span class="text-sm">
                    {{row.judgementDate ? (row.judgementDate | date: 'mediumDate') : '—'}}
                  </span>
                </div>
                }
              </td>
            </ng-container>

            <!-- Actions Column -->
            <ng-container matColumnDef="actions">
              <th mat-header-cell *matHeaderCellDef class="whitespace-nowrap px-4 !text-end !w-44"> Actions </th>
              <td mat-cell *matCellDef="let row" class="px-4 !w-44">
                <div class="flex items-center justify-end space-x-1 min-w-[100px] h-full">
                  @if(row.editedLoader) {
                  <mat-progress-spinner mode="indeterminate" diameter="24"></mat-progress-spinner>
                  } @else {
                  @if(row.editable) {
                  <button mat-icon-button class="action-btn save-btn" matTooltip="Save Changes"
                    [disabled]="isSaveDisabled(row)" (click)="onSave(row)">
                    <mat-icon fontIcon="check_circle"></mat-icon>
                  </button>
                  <button mat-icon-button class="action-btn cancel-btn" matTooltip="Cancel" (click)="onCancel(row)">
                    <mat-icon fontIcon="cancel"></mat-icon>
                  </button>
                  } @else {
                  <button mat-icon-button class="action-btn edit-btn" matTooltip="Edit Judgement Date"
                    (click)="onEditToggle(row)">
                    <mat-icon fontIcon="edit"></mat-icon>
                  </button>
                  }
                  }
                </div>
              </td>
            </ng-container>

            <tr mat-header-row *matHeaderRowDef="displayedColumns; sticky: true"></tr>
            <tr mat-row *matRowDef="let row; columns: displayedColumns;" [class.editing-mode]="row.editable"
              class="hover:bg-gray-50 border-b border-gray-100">
            </tr>
          </table>
        </cdk-virtual-scroll-viewport>
      </div>
      } @else {
      <div class="flex justify-center items-center h-40">
        <mat-progress-spinner mode="indeterminate" diameter="36"></mat-progress-spinner>
      </div>
      }
    </div>
  </div>
</div>

@if (caseNumber()) {
<div class="mt-4">
  <app-case-documents [caseNumber]="caseNumber()" [isSupreme]="isSupremeFlag"></app-case-documents>
</div>
}